import { useNavigate } from 'react-router-dom';
import {
  CalendarDays,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  MoreVertical,
  PencilLine,
  Plus,
  UserRound,
  XCircle,
} from 'lucide-react';
import { EmptyState } from '@/components/ui/EmptyState';
import { Menu, type MenuItem } from '@/components/ui/Menu';
import { Skeleton } from '@/components/ui/Skeleton';
import { StaffIdentity, type StaffIdentityMember } from '@/features/institute/staff/components/StaffIdentity';
import { LessonNowNext } from '@/features/institute/staff/components/LessonNowNext';
import { cn, getNameInitials } from '@/lib/utils';
import type { StaffLessonNow } from '@/types/staff';
import type { StaffPunch } from '@/types/staffAttendance';

/**
 * What a row with no punch and no leave is called. Today it is only "not in
 * yet" — the day is still running; on a past day it is simply no punch-in,
 * which may be an absence or a day the school was shut, and the day list does
 * not say which.
 */
export function noPunchLabel(isToday: boolean): string {
  return isToday ? 'Not in yet' : 'No punch-in';
}

export interface PunchInRowItem {
  id: string;
  uuid: string;
  staffName: string;
  staffUuid: string;
  /** Empty when the record has no employee ID yet. */
  employeeId: string;
  /** The account type — Teacher, Accountant… */
  role: string;
  /** The designation typed on the record, or the account type when none was. */
  designation: string;
  punchIn: string | null;
  punchOut: string | null;
  workingHours: string | null;
  /** `NOT_IN`: no punch on this day and not on leave. */
  status: 'APPROVED' | 'PENDING' | 'REJECTED' | 'ON_LEAVE' | 'NOT_IN';
  punch?: StaffPunch;
  /** The office's note on this day — punched in or not. Null: none. */
  note: string | null;
  /** The staff register's row, so the first column reads as it does there. */
  member?: StaffIdentityMember;
  /** The lesson they are in now and the next one today, from the staff list. */
  nowTeaching?: StaffLessonNow | null;
  nextClass?: StaffLessonNow | null;
  /** Whether they teach at all, so "no more classes" is said only of a teacher. */
  teaches?: boolean;
}

export interface PunchInsTableProps {
  rows: PunchInRowItem[];
  isLoading: boolean;
  canApprove: boolean;
  /** `STAFF_VIEW` — the menu's links lead to the staff profile, which needs it. */
  canViewStaff: boolean;
  currentUserUuid?: string;
  decidingUuid: string | null;
  onDecide: (punch: StaffPunch, status: 'APPROVED' | 'REJECTED') => void;
  /** Opens the note editor. The same key as deciding — see `canApprove`. */
  onEditNote: (row: PunchInRowItem) => void;
  /**
   * The Now / Next column. Today only: the timetable answer is about this
   * moment, and against a past day it would read as where they were then.
   */
  showLessons?: boolean;
  /** Whether the day on screen is today — what a row with no punch is called. */
  isToday: boolean;
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
}

export function PunchInsTable({
  rows,
  isLoading,
  canApprove,
  canViewStaff,
  currentUserUuid,
  decidingUuid,
  onDecide,
  onEditNote,
  showLessons = false,
  isToday,
  page,
  pageSize,
  total,
  onPageChange,
}: PunchInsTableProps) {
  const navigate = useNavigate();

  /**
   * The row's menu: where to read more about this person, then the review. A
   * punch already decided offers the other decision here — deciding again is
   * how a mistake is undone — and one that cannot be reviewed says why not, so
   * the menu never opens onto nothing.
   */
  function rowMenuItems(row: PunchInRowItem, isSelf: boolean, isBusy: boolean): MenuItem[] {
    const items: MenuItem[] = [];

    if (canViewStaff) {
      items.push({
        key: 'profile',
        label: 'View staff profile',
        icon: <UserRound className="h-4 w-4" />,
        onSelect: () => navigate(`/admin/staff?profile=${row.staffUuid}&from=attendance`, { state: { from: '/admin/staff-attendance' } }),
      });
      // Not on your own row: the admin reading this register is not someone
      // whose attendance the institute takes, so there is no history to open.
      if (!isSelf) {
        items.push({
          key: 'history',
          label: 'View attendance history',
          icon: <CalendarDays className="h-4 w-4" />,
          onSelect: () => navigate(`/admin/staff?profile=${row.staffUuid}&tab=Attendance&from=attendance`, { state: { from: '/admin/staff-attendance' } }),
        });
      }
    }

    const punch = row.punch;
    if (canApprove && punch && !isSelf) {
      if (row.status !== 'APPROVED') {
        items.push({
          key: 'approve',
          label: 'Approve punch-in',
          icon: <CheckCircle2 className="h-4 w-4" />,
          onSelect: () => onDecide(punch, 'APPROVED'),
          disabled: isBusy,
        });
      }
      if (row.status !== 'REJECTED') {
        items.push({
          key: 'refuse',
          label: 'Refuse punch-in',
          icon: <XCircle className="h-4 w-4" />,
          tone: 'danger',
          onSelect: () => onDecide(punch, 'REJECTED'),
          disabled: isBusy,
        });
      }
      return items;
    }

    const reason = !row.punch
      ? row.status === 'ON_LEAVE'
        ? 'On leave — there is no punch-in to review'
        : 'No punch-in recorded for this day'
      : isSelf
        ? 'Your own punch-in is reviewed by another admin'
        : 'You do not have permission to review punch-ins';

    items.push({
      key: 'review',
      label: 'Review punch-in',
      description: reason,
      icon: <CheckCircle2 className="h-4 w-4" />,
      onSelect: () => undefined,
      disabled: true,
    });

    return items;
  }

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const startEntry = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const endEntry = Math.min(page * pageSize, total);

  return (
    <div className="rounded-3xl bg-white dark:bg-[#111c2e] border border-slate-200/90 dark:border-slate-800 shadow-xs overflow-hidden flex flex-col">
      <div className="w-full overflow-x-auto custom-scrollbar">
        <table className="w-full text-left border-collapse min-w-[700px]">
          <thead>
            <tr className="border-b border-slate-200 dark:border-slate-800 bg-slate-100 dark:bg-[#0b1322] text-[13.5px] font-bold tracking-wide text-slate-700 dark:text-slate-300">
              <th scope="col" className="sm:sticky sm:left-0 z-30 py-3.5 px-3 w-12 min-w-[48px] max-w-[48px] text-center bg-slate-100 dark:bg-[#0b1322]">#</th>
              <th scope="col" className="sm:sticky sm:left-[48px] z-30 py-3.5 px-4 min-w-[220px] bg-slate-100 dark:bg-[#0b1322] sm:border-r border-slate-200 dark:border-slate-800 sm:shadow-[4px_0_8px_-3px_rgba(0,0,0,0.06)] dark:sm:shadow-[4px_0_8px_-3px_rgba(0,0,0,0.3)]">Staff Name</th>
              <th scope="col" className="py-3.5 px-4 min-w-[110px]">Punch In</th>
              <th scope="col" className="py-3.5 px-4 min-w-[110px]">Punch Out</th>
              <th scope="col" className="py-3.5 px-4 min-w-[130px]">Working Hours</th>
              <th scope="col" className="py-3.5 px-4 text-center min-w-[120px]">Status</th>
              {showLessons && (
                <th scope="col" className="py-3.5 px-4 min-w-[200px]">Now / Next</th>
              )}
              <th scope="col" className="py-3.5 px-4 min-w-[200px]">Notes</th>
              <th scope="col" className="sm:sticky sm:right-0 z-30 py-3.5 px-4 text-right min-w-[90px] bg-slate-100 dark:bg-[#0b1322] sm:border-l border-slate-200 dark:border-slate-800 sm:shadow-[-4px_0_8px_-3px_rgba(0,0,0,0.06)] dark:sm:shadow-[-4px_0_8px_-3px_rgba(0,0,0,0.3)]">Action</th>
            </tr>
          </thead>

          <tbody className="divide-y divide-slate-100 dark:divide-slate-800/60 font-medium">
            {isLoading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <tr key={i} className="group border-b border-slate-200/80 dark:border-slate-800/80 bg-white dark:bg-[#111c2e] animate-pulse">
                  <td className="sm:sticky sm:left-0 z-20 py-3.5 px-3 w-12 min-w-[48px] max-w-[48px] bg-white dark:bg-[#111c2e]"><Skeleton className="h-4 w-5 mx-auto" /></td>
                  <td className="sm:sticky sm:left-[48px] z-20 py-3.5 px-4 min-w-[220px] bg-white dark:bg-[#111c2e] sm:border-r border-slate-200 dark:border-slate-800"><Skeleton className="h-4 w-32 mb-1.5" /><Skeleton className="h-3 w-44" /></td>
                  <td className="py-3.5 px-4"><Skeleton className="h-4 w-16" /></td>
                  <td className="py-3.5 px-4"><Skeleton className="h-4 w-16" /></td>
                  <td className="py-3.5 px-4"><Skeleton className="h-4 w-16" /></td>
                  <td className="py-3.5 px-4 text-center"><Skeleton className="h-6 w-20 mx-auto rounded-full" /></td>
                  {showLessons && <td className="py-3.5 px-4"><Skeleton className="h-4 w-32" /></td>}
                  <td className="py-3.5 px-4"><Skeleton className="h-4 w-28" /></td>
                  <td className="sm:sticky sm:right-0 z-20 py-3.5 px-4 text-right min-w-[90px] bg-white dark:bg-[#111c2e] sm:border-l border-slate-200 dark:border-slate-800"><Skeleton className="h-8 w-8 ml-auto rounded-lg" /></td>
                </tr>
              ))
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={showLessons ? 9 : 8} className="py-12 text-center">
                  <EmptyState
                    title="No staff attendance records found"
                    description="No staff punch-ins match the selected filters or date."
                  />
                </td>
              </tr>
            ) : (
              rows.map((row, idx) => {
                const isSelf = row.staffUuid === currentUserUuid;
                const isBusy = decidingUuid === row.uuid;
                const punch = row.punch;

                return (
                  <tr
                    key={row.id}
                    className="group border-b border-slate-200/80 dark:border-slate-800/80 bg-white dark:bg-[#111c2e] hover:bg-slate-50/80 dark:hover:bg-[#16253d] transition-colors"
                  >
                    {/* Index */}
                    <td className="sm:sticky sm:left-0 z-20 py-3 px-3 w-12 min-w-[48px] max-w-[48px] text-center text-sm font-bold text-slate-400 bg-white dark:bg-[#111c2e] group-hover:bg-slate-50 dark:group-hover:bg-[#16253d]">
                      {(page - 1) * pageSize + idx + 1}
                    </td>

                    {/* The staff register's first column, and their designation. */}
                    <td className="sm:sticky sm:left-[48px] z-20 py-2.5 px-4 min-w-[240px] bg-white dark:bg-[#111c2e] group-hover:bg-slate-50 dark:group-hover:bg-[#16253d] sm:border-r border-slate-200 dark:border-slate-800 sm:shadow-[4px_0_8px_-3px_rgba(0,0,0,0.06)] dark:sm:shadow-[4px_0_8px_-3px_rgba(0,0,0,0.3)]">
                      {row.member ? (
                        <StaffIdentity
                          member={row.member}
                          badge={
                            isSelf && (
                              <span className="px-1.5 py-0.5 rounded text-[10px] font-extrabold bg-blue-50 dark:bg-blue-950/80 text-blue-600 dark:text-blue-400">
                                You
                              </span>
                            )
                          }
                          // The designation on the record, or the role when none was
                          // typed — never a job title made up from the subjects taught.
                          footer={
                            <div className="text-xs text-slate-500 dark:text-slate-400 font-medium truncate">{row.designation}</div>
                          }
                        />
                      ) : (
                        // Only while the staff list has not arrived: the punch
                        // alone names the person and nothing more.
                        <div className="flex items-start gap-2.5">
                          <span className="grid h-8 w-8 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-blue-500 via-indigo-600 to-sky-600 text-[11px] font-black text-white shadow-2xs mt-0.5">
                            {getNameInitials(row.staffName)}
                          </span>
                          <div className="min-w-0">
                            <p className="font-extrabold text-sm text-slate-700 dark:text-white flex items-center gap-1.5 truncate">
                              {row.staffName}
                              {isSelf && (
                                <span className="px-1.5 py-0.5 rounded text-[10px] font-extrabold bg-blue-50 dark:bg-blue-950/80 text-blue-600 dark:text-blue-400">
                                You
                              </span>
                              )}
                            </p>
                            <span className="truncate text-xs text-slate-500 dark:text-slate-400 font-medium">{row.designation}</span>
                          </div>
                        </div>
                      )}
                    </td>

                    {/* Punch In */}
                    <td className="py-3 px-4 text-sm font-mono font-bold whitespace-nowrap">
                      {row.punchIn ? (
                        <span className={row.status === 'PENDING' ? 'text-amber-600 dark:text-amber-400' : 'text-emerald-600 dark:text-emerald-400'}>
                          {row.punchIn}
                        </span>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>

                    {/* Punch Out */}
                    <td className="py-3 px-4 text-sm font-mono font-bold whitespace-nowrap">
                      {row.punchOut ? (
                        <span className="text-emerald-600 dark:text-emerald-400">
                          {row.punchOut}
                          {row.punch?.isAutoPunchOut && (
                            <span className="ml-1 font-sans text-xs font-semibold text-ink-3">auto</span>
                          )}
                        </span>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>

                    {/* Working Hours */}
                    <td className="py-3 px-4 text-sm font-mono font-semibold text-slate-600 dark:text-slate-300 whitespace-nowrap">
                      {row.workingHours ? row.workingHours : <span className="text-slate-400">—</span>}
                    </td>

                    {/* Status Badge */}
                    <td className="py-3 px-4 text-center whitespace-nowrap">
                      {row.status === 'APPROVED' ? (
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-sm font-bold bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
                          Present
                        </span>
                      ) : row.status === 'PENDING' ? (
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-sm font-bold bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20">
                          Awaiting check
                        </span>
                      ) : row.status === 'ON_LEAVE' ? (
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-sm font-bold bg-purple-500/10 text-purple-600 dark:text-purple-400 border border-purple-500/20">
                          On Leave
                        </span>
                      ) : (
                        // A refused punch is still a punch — naming it "Absent"
                        // would hide that somebody did sign in and was turned down.
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-sm font-bold bg-rose-500/10 text-rose-600 dark:text-rose-400 border border-rose-500/20">
                          {row.status === 'NOT_IN' ? noPunchLabel(isToday) : 'Not accepted'}
                        </span>
                      )}
                    </td>

                    {/* Where they are this period and where they go next, today. */}
                    {showLessons && (
                      <td className="py-2.5 px-4 min-w-[200px]">
                        <LessonNowNext now={row.nowTeaching} next={row.nextClass} teaches={Boolean(row.teaches)} />
                      </td>
                    )}

                    {/* The office's note — on any day, punched in or not. */}
                    <td className="py-3 px-4 min-w-[200px] max-w-[280px]">
                      <div className="flex items-start gap-1.5">
                        {row.note && (
                          <span className="line-clamp-2 text-xs font-medium text-slate-600 dark:text-slate-300" title={row.note}>
                            {row.note}
                          </span>
                        )}
                        {canApprove && !isSelf && (
                          <button
                            type="button"
                            onClick={() => onEditNote(row)}
                            aria-label={row.note ? `Edit the note on ${row.staffName}'s day` : `Add a note on ${row.staffName}'s day`}
                            title={row.note ? 'Edit note' : 'Add note'}
                            className={cn(
                              'inline-flex shrink-0 items-center gap-1 rounded-lg text-sm font-bold text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-950/60 transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500',
                              row.note ? 'p-1' : 'px-1.5 py-0.5',
                            )}
                          >
                            {row.note ? (
                              <PencilLine className="h-3.5 w-3.5" aria-hidden />
                            ) : (
                              <>
                                <Plus className="h-3.5 w-3.5" aria-hidden />
                                Add note
                              </>
                            )}
                          </button>
                        )}
                        {!row.note && !(canApprove && !isSelf) && <span className="text-slate-400">—</span>}
                      </div>
                    </td>

                    {/* Actions */}
                    <td className="sm:sticky sm:right-0 z-20 py-3 px-4 text-right min-w-[90px] bg-white dark:bg-[#111c2e] group-hover:bg-slate-50 dark:group-hover:bg-[#16253d] sm:border-l border-slate-200 dark:border-slate-800 sm:shadow-[-4px_0_8px_-3px_rgba(0,0,0,0.06)] dark:sm:shadow-[-4px_0_8px_-3px_rgba(0,0,0,0.3)]">
                      <div className="relative inline-block text-left">
                        {/* Only a punch still awaiting its check gets the buttons inline; once decided it reads like every other row. */}
                        {canApprove && punch && !isSelf && row.status === 'PENDING' ? (
                          <div className="flex items-center justify-end gap-1">
                            <button
                              type="button"
                              disabled={isBusy}
                              onClick={() => onDecide(punch, 'APPROVED')}
                              title="Approve Punch-in"
                              className="p-1.5 rounded-lg text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-950/60 transition-colors cursor-pointer disabled:opacity-50"
                            >
                              <CheckCircle2 className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              disabled={isBusy}
                              onClick={() => onDecide(punch, 'REJECTED')}
                              title="Refuse Punch-in"
                              className="p-1.5 rounded-lg text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/60 transition-colors cursor-pointer disabled:opacity-50"
                            >
                              <XCircle className="h-4 w-4" />
                            </button>
                          </div>
                        ) : (
                          <Menu
                            label={`Actions for ${row.staffName}`}
                            trigger={<MoreVertical className="h-4 w-4" aria-hidden />}
                            items={rowMenuItems(row, isSelf, isBusy)}
                            disabled={isBusy}
                            triggerClassName="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer"
                          />
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Footer & Pagination */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3 p-4 border-t border-slate-200/80 dark:border-slate-800 bg-slate-50/50 dark:bg-[#0b1322]/50 text-xs text-slate-500 dark:text-slate-400">
        <div>
          Showing <span className="font-bold text-slate-700 dark:text-slate-200">{startEntry}</span> to{' '}
          <span className="font-bold text-slate-700 dark:text-slate-200">{endEntry}</span> of{' '}
          <span className="font-bold text-slate-700 dark:text-slate-200">{total}</span> entries
        </div>

        <div className="flex items-center gap-1">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => onPageChange(page - 1)}
            className="p-1.5 rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#111c2e] hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>

          {Array.from({ length: Math.min(totalPages, 5) }).map((_, i) => {
            const pageNumber = i + 1;
            const isActive = page === pageNumber;
            return (
              <button
                key={pageNumber}
                type="button"
                onClick={() => onPageChange(pageNumber)}
                className={cn(
                  'h-8 w-8 rounded-lg text-xs font-bold transition-all',
                  isActive
                    ? 'bg-emerald-500 text-white shadow-xs'
                    : 'border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#111c2e] hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300',
                )}
              >
                {pageNumber}
              </button>
            );
          })}

          {totalPages > 5 && (
            <>
              <span className="px-1 text-slate-400">...</span>
              <button
                type="button"
                onClick={() => onPageChange(totalPages)}
                className={cn(
                  'h-8 w-8 rounded-lg text-xs font-bold transition-all',
                  page === totalPages
                    ? 'bg-emerald-500 text-white shadow-xs'
                    : 'border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#111c2e] hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300',
                )}
              >
                {totalPages}
              </button>
            </>
          )}

          <button
            type="button"
            disabled={page >= totalPages}
            onClick={() => onPageChange(page + 1)}
            className="p-1.5 rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#111c2e] hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
